import { motion } from "framer-motion";
import useIsMobile from "../hooks/useIsMobile";
import Reveal from "../components/common/Reveal";
import SectionTag from "../components/common/SectionTag";
import Icon from "../components/common/Icon";
import Seo from "../components/common/Seo";
import { colors, fonts, emphasis, sectionPad, maxW, springHover, cellHover, iconPop } from "../styles/theme";

const PILLARS = [
  { icon: "Target", title: "Our Mission", text: "To empower businesses of every size with cutting-edge digital solutions, and nurture the next generation of tech talent through meaningful, hands-on internship experiences." },
  { icon: "Telescope", title: "Our Vision", text: "To become a trusted digital innovation partner across India — known for excellence, integrity, and creating lasting impact for our clients and community." },
];

const VALUES = [
  { icon: "Zap", title: "Speed", desc: "We deliver fast without cutting corners." },
  { icon: "Gem", title: "Quality", desc: "Every pixel and every line of code matters." },
  { icon: "Handshake", title: "Integrity", desc: "Honest work, transparent communication." },
  { icon: "Rocket", title: "Innovation", desc: "Always pushing what's possible." },
];

export default function AboutPage() {
  const mob = useIsMobile();

  return (
    <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74 }}>
      <Seo
        title="About Us | Charith"
        description="Charith was founded to bridge the gap between innovative ideas and powerful digital execution — serving clients across India and beyond."
        path="/about"
      />
      <section style={{ background: colors.paper, ...sectionPad(mob) }}>
        <div style={{ ...maxW }}>
          <Reveal style={{ maxWidth: 720 }}>
            <SectionTag>About Us</SectionTag>
            <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 34 : "clamp(32px,4.4vw,58px)", lineHeight: 1.12, color: colors.ink, letterSpacing: "-.01em", marginBottom: 22 }}>
              We build digital products that <span style={emphasis}>matter</span>
            </h1>
            <p style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: mob ? 15.5 : 18, lineHeight: 1.8, maxWidth: 580 }}>
              Charith was founded with a single mission: bridge the gap between innovative ideas and powerful digital execution. We serve clients across India and beyond.
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ background: colors.paper, paddingBottom: mob ? 56 : 96, padding: mob ? "0 6% 56px" : "0 6% 96px" }}>
        <div style={{ ...maxW, display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: 0, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, overflow: "hidden" }}>
          {PILLARS.map((item, i) => (
            <Reveal key={i} delay={i * 0.12} style={{ borderLeft: (!mob && i > 0) ? `1px solid ${colors.borderStrong}` : "none", borderTop: (mob && i > 0) ? `1px solid ${colors.borderStrong}` : "none" }}>
              <motion.div initial="rest" whileHover="hover" variants={cellHover} transition={springHover} style={{ padding: mob ? 28 : 44, height: "100%" }}>
                <motion.div variants={iconPop} transition={springHover} style={{ display: "inline-flex", marginBottom: 18 }}>
                  <Icon name={item.icon} size={26} style={{ color: colors.rust }} />
                </motion.div>
                <h3 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 20 : 24, color: colors.ink, marginBottom: 14 }}>{item.title}</h3>
                <p style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: mob ? 14.5 : 15.5, lineHeight: 1.8 }}>{item.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      <section style={{ background: colors.paperAlt, borderTop: `1px solid ${colors.border}`, ...sectionPad(mob) }}>
        <div style={{ ...maxW }}>
          <Reveal style={{ marginBottom: mob ? 34 : 50 }}>
            <SectionTag>How We Work</SectionTag>
            <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 28 : 38, color: colors.ink }}>Our Core Values</h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr 1fr" : "repeat(4,1fr)", gap: mob ? 20 : 0 }}>
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 0.07} style={{ borderLeft: (!mob && i > 0) ? `1px solid ${colors.borderStrong}` : "none" }}>
                <motion.div initial="rest" whileHover="hover" variants={cellHover} transition={springHover} style={{ padding: mob ? "10px 0" : "18px 26px", height: "100%" }}>
                  <motion.div variants={iconPop} transition={springHover} style={{ display: "inline-flex", marginBottom: 14 }}>
                    <Icon name={v.icon} size={24} style={{ color: colors.rust }} />
                  </motion.div>
                  <h4 style={{ fontFamily: fonts.body, fontWeight: 700, fontSize: mob ? 15 : 16.5, color: colors.ink, marginBottom: 8 }}>{v.title}</h4>
                  <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 13.5, lineHeight: 1.65 }}>{v.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
